/**
 * System Permissions Utility
 * Handles permission checking for system-level administrative access
 */

/**
 * System user roles
 */
export const SYSTEM_ROLES = {
  ROOT_ADMIN: 'root_admin',         // Christopher (founder) - full access
  SYSTEM_ADMIN: 'system_admin',     // Core team - most access
  SUPPORT_AGENT: 'support_agent',   // Customer support - limited access
  DEVELOPER: 'developer',           // Development team - view logs, test features
  UX_DESIGNER: 'ux_designer'        // Design team - view analytics, test features
};

/**
 * System permission types
 */
export const SYSTEM_PERMISSIONS = {
  // User Management
  MANAGE_USERS: 'canManageUsers',
  VIEW_USERS: 'canViewUsers',
  DELETE_USERS: 'canDeleteUsers',

  // System Notifications
  CREATE_SYSTEM_NOTIFICATIONS: 'canCreateSystemNotifications',
  MANAGE_SYSTEM_NOTIFICATIONS: 'canManageSystemNotifications',

  // Security & Audit
  VIEW_AUDIT_LOGS: 'canViewAuditLogs',
  MANAGE_MFA: 'canManageMFA',
  MANAGE_PERMISSIONS: 'canManagePermissions',

  // Support
  VIEW_SUPPORT_TICKETS: 'canViewSupportTickets',
  RESPOND_TO_TICKETS: 'canRespondToTickets',

  // System Configuration
  MANAGE_SERVICES: 'canManageServices',
  VIEW_ANALYTICS: 'canViewAnalytics',

  // Family Management (system-level oversight)
  VIEW_ALL_FAMILIES: 'canViewAllFamilies',
  MANAGE_FAMILIES: 'canManageFamilies'
};

/**
 * Default permissions for each system role
 */
export const DEFAULT_ROLE_PERMISSIONS = {
  [SYSTEM_ROLES.ROOT_ADMIN]: {
    // Full access to everything
    canManageUsers: true,
    canViewUsers: true,
    canDeleteUsers: true,
    canCreateSystemNotifications: true,
    canManageSystemNotifications: true,
    canViewAuditLogs: true,
    canManageMFA: true,
    canManagePermissions: true,
    canViewSupportTickets: true,
    canRespondToTickets: true,
    canManageServices: true,
    canViewAnalytics: true,
    canViewAllFamilies: true,
    canManageFamilies: true
  },

  [SYSTEM_ROLES.SYSTEM_ADMIN]: {
    // Most access, but can't manage permissions or delete users
    canManageUsers: true,
    canViewUsers: true,
    canDeleteUsers: false,
    canCreateSystemNotifications: true,
    canManageSystemNotifications: true,
    canViewAuditLogs: true,
    canManageMFA: true,
    canManagePermissions: false,
    canViewSupportTickets: true,
    canRespondToTickets: true,
    canManageServices: false,
    canViewAnalytics: true,
    canViewAllFamilies: true,
    canManageFamilies: true
  },

  [SYSTEM_ROLES.SUPPORT_AGENT]: {
    // Support-focused permissions
    canManageUsers: false,
    canViewUsers: true,
    canDeleteUsers: false,
    canCreateSystemNotifications: false,
    canManageSystemNotifications: false,
    canViewAuditLogs: false,
    canManageMFA: false,
    canManagePermissions: false,
    canViewSupportTickets: true,
    canRespondToTickets: true,
    canManageServices: false,
    canViewAnalytics: true,
    canViewAllFamilies: true,
    canManageFamilies: false
  },

  [SYSTEM_ROLES.DEVELOPER]: {
    // Development-focused permissions
    canManageUsers: false,
    canViewUsers: true,
    canDeleteUsers: false,
    canCreateSystemNotifications: false,
    canManageSystemNotifications: false,
    canViewAuditLogs: true,
    canManageMFA: false,
    canManagePermissions: false,
    canViewSupportTickets: false,
    canRespondToTickets: false,
    canManageServices: false,
    canViewAnalytics: true,
    canViewAllFamilies: false,
    canManageFamilies: false
  },

  [SYSTEM_ROLES.UX_DESIGNER]: {
    // Design-focused permissions
    canManageUsers: false,
    canViewUsers: false,
    canDeleteUsers: false,
    canCreateSystemNotifications: false,
    canManageSystemNotifications: false,
    canViewAuditLogs: false,
    canManageMFA: false,
    canManagePermissions: false,
    canViewSupportTickets: false,
    canRespondToTickets: false,
    canManageServices: false,
    canViewAnalytics: true,
    canViewAllFamilies: false,
    canManageFamilies: false
  }
};

/**
 * Check if a user is a system admin (any type)
 * @param {Object} profile - User profile from Firestore
 * @returns {boolean} True if user is a system admin
 */
export const isSystemAdmin = (profile) => {
  if (!profile) return false;
  return profile.accountType === 'system_admin' || profile.isSuperAdmin === true;
};

/**
 * Check if a user is the root admin
 * @param {Object} profile - User profile from Firestore
 * @returns {boolean} True if user is root admin
 */
export const isRootAdmin = (profile) => {
  if (!profile) return false;
  return profile.isSuperAdmin === true || profile.role === SYSTEM_ROLES.ROOT_ADMIN;
};

/**
 * Check if a user has a specific system permission
 * @param {Object} profile - User profile from Firestore
 * @param {string} permission - Permission key from SYSTEM_PERMISSIONS
 * @returns {boolean} True if user has permission
 */
export const hasSystemPermission = (profile, permission) => {
  if (!profile) return false;

  // Root admin has all permissions
  if (isRootAdmin(profile)) return true;

  // Check if user is a system admin
  if (!isSystemAdmin(profile)) return false;

  // Check specific permission
  if (profile.systemPermissions && profile.systemPermissions[permission]) {
    return true;
  }

  return false;
};

/**
 * Check if a user has any of the specified permissions
 * @param {Object} profile - User profile from Firestore
 * @param {string[]} permissions - Array of permission keys
 * @returns {boolean} True if user has at least one permission
 */
export const hasAnySystemPermission = (profile, permissions) => {
  if (!profile || !permissions || permissions.length === 0) return false;

  // Root admin has all permissions
  if (isRootAdmin(profile)) return true;

  return permissions.some(permission => hasSystemPermission(profile, permission));
};

/**
 * Check if a user has all of the specified permissions
 * @param {Object} profile - User profile from Firestore
 * @param {string[]} permissions - Array of permission keys
 * @returns {boolean} True if user has all permissions
 */
export const hasAllSystemPermissions = (profile, permissions) => {
  if (!profile || !permissions || permissions.length === 0) return false;

  // Root admin has all permissions
  if (isRootAdmin(profile)) return true;

  return permissions.every(permission => hasSystemPermission(profile, permission));
};

/**
 * Get the display name for a system role
 * @param {string} role - Role key
 * @returns {string} Display name
 */
export const getSystemRoleDisplayName = (role) => {
  const displayNames = {
    [SYSTEM_ROLES.ROOT_ADMIN]: 'Root Administrator',
    [SYSTEM_ROLES.SYSTEM_ADMIN]: 'System Administrator',
    [SYSTEM_ROLES.SUPPORT_AGENT]: 'Support Agent',
    [SYSTEM_ROLES.DEVELOPER]: 'Developer',
    [SYSTEM_ROLES.UX_DESIGNER]: 'UX Designer'
  };

  return displayNames[role] || role;
};

/**
 * Get all permissions for a role
 * @param {string} role - Role key
 * @returns {Object} Permissions object
 */
export const getPermissionsForRole = (role) => {
  return DEFAULT_ROLE_PERMISSIONS[role] || {};
};

const systemPermissions = {
  SYSTEM_ROLES,
  SYSTEM_PERMISSIONS,
  DEFAULT_ROLE_PERMISSIONS,
  isSystemAdmin,
  isRootAdmin,
  hasSystemPermission,
  hasAnySystemPermission,
  hasAllSystemPermissions,
  getSystemRoleDisplayName,
  getPermissionsForRole
};

export default systemPermissions;
